import api, {
    Derogatory,
    bankruptcyTypeDisplay,
    borrowerTypeDisplay,
    derogatoryTypeDisplay,
    dispositionTypeDisplay
} from '@api';
import { Edit, RemoveCircleOutline } from '@mui/icons-material';
import { Chip, Typography } from '@mui/material';
import { formatCurrency, formatDate, useParams } from '@tsp-ui/core';
import {
    ExpandableHeader, IconButton, LabelGroup, LabeledValue, usePageMessage
} from '@tsp-ui/core/components';
import { LoanDetailPageParams } from '@views/loans/LoanDetailPage';
import { useContext, useState } from 'react';
import { Link } from 'react-router-dom';

import styles from './DerogatoryCard.module.scss';
import { DerogatoryContext } from './DerogatorySection';


interface DerogatoryCardProps {
    derogatory: Derogatory;
}

export default function DerogatoryCard({ derogatory }: DerogatoryCardProps) {
    const { accountID: clientId, loanID: loanId } = useParams<LoanDetailPageParams>();
    const pageMessage = usePageMessage();
    const [ deleteLoading, setDeleteLoading ] = useState(false);
    const { setDerogatories } = useContext(DerogatoryContext);

    async function deleteDerogatory(derogatoryId: string) {
        setDeleteLoading(true);

        try {
            await api.derogatory.deleteDerogatory(clientId, loanId, derogatoryId);
            setDerogatories((records) => records.filter((record) => record.id !== derogatoryId));
            pageMessage.success('Derogatory record deleted');
        } catch (error) {
            pageMessage.handleApiError('An error occurred while deleting derogatory record', error);
        }

        setDeleteLoading(false);
    }

    return (
        <ExpandableHeader
            expandedVariant="contained"
            title={(
                <div className={styles.root}>
                    <div className={styles.headerRow}>
                        <Typography fontWeight="500">
                            {derogatoryTypeDisplay[derogatory.type]}
                        </Typography>

                        <Chip
                            size="small"
                            label={borrowerTypeDisplay[derogatory.borrower]}
                            color="primary"
                            variant="outlined"
                            className={styles.borrowerChip}
                        />
                    </div>

                    <div className={styles.iconButtons}>
                        <IconButton
                            tooltip="Edit"
                            size="small"
                            component={Link}
                            to={`derogatory/${derogatory.id}`}
                        >
                            <Edit color="secondary" />
                        </IconButton>

                        <IconButton
                            tooltip="Delete"
                            loading={deleteLoading}
                            size="small"
                            onClick={() => deleteDerogatory(derogatory.id)}
                        >
                            <RemoveCircleOutline color="error" />
                        </IconButton>
                    </div>
                </div>
            )}
            expandedContent={(
                <>
                    <div>
                        {derogatory.date && (
                            <Typography
                                variant="body2"
                                color="textSecondary"
                            >
                                Filed on {formatDate(derogatory.date)}
                            </Typography>
                        )}
                    </div>

                    <LabelGroup className={styles.labelGroup}>
                        {derogatory.company && (
                            <LabeledValue
                                label="Company:"
                                value={derogatory.company}
                            />
                        )}

                        {derogatory.plaintiff && (
                            <LabeledValue
                                label="Plaintiff:"
                                value={derogatory.plaintiff}
                            />
                        )}

                        {(derogatory.amount !== undefined && derogatory.amount !== null) && (
                            <LabeledValue
                                label="Amount:"
                                value={formatCurrency(derogatory.amount)}
                            />
                        )}

                        {derogatory.accountNumber && (
                            <LabeledValue
                                label="Account Number:"
                                value={derogatory.accountNumber}
                            />
                        )}

                        {derogatory.bankruptcyType && (
                            <LabeledValue
                                label="Bankruptcy Type:"
                                value={bankruptcyTypeDisplay[derogatory.bankruptcyType]}
                            />
                        )}

                        {derogatory.disposition && (
                            <LabeledValue
                                label="Disposition:"
                                value={dispositionTypeDisplay[derogatory.disposition]}
                            />
                        )}

                        {derogatory.payingOff !== undefined && (
                            <LabeledValue
                                label="Paying Off:"
                                value={derogatory.payingOff ? 'Yes' : 'No'}
                            />
                        )}
                    </LabelGroup>

                    {derogatory.extenuating && (
                        <div className={styles.extenuatingCircumstances}>
                            <Typography
                                variant="caption"
                                color="textSecondary"
                            >
                                Extenuating Circumstances
                            </Typography>

                            <Typography
                                variant="body2"
                                fontStyle="italic"
                            >
                                {derogatory.extenuating}
                            </Typography>
                        </div>
                    )}
                </>
            )}
        />
    );
}
